import { Link } from 'react-router-dom';
import { CalendarDays, ChevronDown, Target, Video } from 'lucide-react';
import { CalloutPin, SatRealScreen, SurfaceFrame, type Callout } from '@/components/SatRealScreen';
import { SatIntakeFormCard } from '@/modules/marketing/landing/SatIntakeFormCard';
import { Button } from '@/components/ui/button';

/**
 * Tutoring Real Screen (spec 010 §5.4). The course is free; tutoring is paid and optional.
 * `id="tutoring"` anchors this section (the hero link now goes to the tutoring how-it-works page).
 * The tutor is not named: this is an example session, not a real person.
 */
const CALLOUTS: readonly Callout[] = [
  { title: 'Starts from your results', body: 'Your tutor can begin from your diagnostic, so sessions skip the generic lesson.' },
  { title: 'Booked on Mentara', body: 'Pick a time, your tutor confirms, and the video link is ready to join.' },
  { title: 'A note after every session', body: 'You get what you covered and what to do next.' },
];

function SessionSurface() {
  return (
    <SurfaceFrame url="mentaratutors.com/sessions">
      <div className="space-y-4 bg-background p-4 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <p className="text-lg font-bold tracking-tight">SAT session</p>
          <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400">
            Confirmed
          </span>
        </div>
        <ul className="space-y-3 text-sm">
          <li className="flex items-start gap-2.5">
            <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span>Thursday, 5:00 PM, 60 minutes</span>
          </li>
          <li className="flex items-start gap-2.5">
            <CalloutPin n={1} className="mt-px" />
            <span className="flex items-start gap-2">
              <Target className="mt-0.5 hidden h-4 w-4 shrink-0 text-muted-foreground sm:block" aria-hidden />
              <span>Focus: Problem Solving and Data Analysis, the area at the top of your results</span>
            </span>
          </li>
          <li className="flex items-start gap-2.5">
            <CalloutPin n={2} className="mt-px" />
            <span className="flex items-center gap-2">
              <Video className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground">Join session</span>
            </span>
          </li>
        </ul>
        <div className="flex items-start gap-2.5 rounded-xl border bg-muted/50 p-4">
          <CalloutPin n={3} className="mt-0.5" />
          <div className="text-sm leading-relaxed">
            <p className="font-semibold">Session notes</p>
            <p className="mt-1 text-muted-foreground">
              Covered: ratios and rates. Next time: word problems, then a short practice set on percentages.
            </p>
          </div>
        </div>
      </div>
    </SurfaceFrame>
  );
}

export function SatTutoringSection() {
  return (
    <div id="tutoring" className="scroll-mt-20">
      <SatRealScreen
        id="tutoring-screen"
        title="A real tutor, whenever you want one"
        lead="The course works on its own. When you want a person, add 1-on-1 SAT sessions. Tutoring is paid, optional, and booked through Mentara."
        callouts={CALLOUTS}
        caption="Example SAT session."
        layout="surface-right"
        tone="border-t bg-muted/40"
        footer={
          <Button asChild variant="outline">
            <Link to="/how-it-works/students/">How tutoring works →</Link>
          </Button>
        }
      >
        <SessionSurface />
      </SatRealScreen>

      <div className="border-b bg-muted/40">
        <div className="mx-auto grid w-full max-w-6xl gap-x-14 px-4 pb-14 sm:px-6 lg:grid-cols-[minmax(0,21rem)_minmax(0,1fr)] lg:pb-16">
          <details className="group rounded-2xl border bg-card lg:col-start-2" data-testid="intake-details">
            <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-3 rounded-2xl px-5 py-3 text-base font-semibold outline-none marker:hidden focus-visible:ring-2 focus-visible:ring-primary [&::-webkit-details-marker]:hidden">
              Want a tutor to set up your plan? Leave your details.
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden />
            </summary>
            <div className="border-t px-5 pb-5 pt-4">
              <SatIntakeFormCard />
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}
